import { AxiosError } from 'axios';
import { Role, User } from '../../types';
import { Formik, Form } from 'formik';
import SchemaProvider from '../../utils/SchemaProvider';
import FormikTextInput from '../formik/FormikTextInput';
import { FormikSelect } from '../formik/FormikSelect';
import UserPolicy from '../../policies/UserPolicy';
import FormikPasswordInput from '../formik/FormikPasswordInput';
import {
    Box,
    Button,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    FormControl,
    FormLabel,
    HStack,
    Switch,
    Text,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { AccountMode } from '../../types/enums/AccountMode';
import { InferType } from 'yup';
import { useCreateEmployee, useUpdateEmployee } from '../../query/resource-hooks/company';
import { useAuthStore } from '../../store/auth/authStore';
import { ComponentProps, useRef } from 'react';
import ErrorHelper from '../../query/utils/ErrorHelper';
import ZoneSelectButton from '../ui/ZoneSelectButton';
import { useSuIntl } from '../../hooks/useSuIntl';

export type EmployeeFormProps = Omit<ComponentProps<typeof Drawer>, 'children'> & {
    onSuccess?: (employee: User) => void;
    onError?: (error: AxiosError) => void;
    updatedUser?: User;
};

export const EmployeeForm: React.FC<EmployeeFormProps> = ({ updatedUser, onSuccess, onError, isOpen, onClose }) => {
    const { t, intl } = useSuIntl();

    const useCreateUser = useCreateEmployee(handleSuccess, handleError);
    const useUpdateUser = useUpdateEmployee(handleSuccess, handleError);
    const toast = useToast();
    const loggedUser = useAuthStore((state) => state.user)!;

    const firstField = useRef<HTMLInputElement | null>(null);

    const validationSchema = updatedUser ? SchemaProvider.getEmployeeUpdateSchema(intl) : SchemaProvider.getNewEmployeeSchema(intl);
    function handleSubmit(values: InferType<typeof validationSchema>) {
        if (updatedUser) {
            useUpdateUser.mutate({
                data: {
                    id: updatedUser.id,
                    firstName: values.firstName,
                    surname: values.surname,
                    role: values.role as Role,
                    password: values.password,
                    settings: {
                        accountMode: (values.mode as AccountMode) || null,
                        defaultZoneId: values.defaultZoneId,
                    },
                },
            });
            return;
        } else {
            const createSchema = SchemaProvider.getNewEmployeeSchema(intl);
            const createData = values as InferType<typeof createSchema>;

            useCreateUser.mutate({
                ...(createData.generateUsername ? {} : { userName: createData.userName! }),
                password: createData.password,
                firstName: createData.firstName,
                ...(createData.role !== Role.TEAM_ACCOUNT && {
                    surname: createData.surname,
                }),
                role: createData.role as Role,
                mode: createData.mode as AccountMode,
                defaultZoneId: createData.defaultZoneId,
                generateUsername: createData.generateUsername,
            });
        }
    }

    function handleSuccess(data: User) {
        toast({
            title: t(updatedUser ? 'employeeUpdated' : 'employeeCreated'),
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top',
        });
        onSuccess?.(data);
        handleClose();
    }

    function handleError(error: AxiosError) {
        const errorMessage = ErrorHelper.mapRequestErrorToIntlKey(error);

        toast({
            title: t(errorMessage),
            status: 'error',
            duration: 8000,
            isClosable: true,
            position: 'top',
        });
        onError?.(error);
    }

    function handleClose() {
        useCreateUser.reset();
        useUpdateUser.reset();
        onClose();
    }

    return (
        <Drawer size={'md'} initialFocusRef={firstField} isOpen={isOpen} onClose={handleClose} placement="right">
            <DrawerOverlay />
            <DrawerContent m={{ sm: 0, md: 2 }} borderRadius={'xl'}>
                <DrawerCloseButton />
                <DrawerHeader borderBottomWidth="1px">{t(updatedUser ? 'user.updateUser' : 'user.createUser')}</DrawerHeader>
                <DrawerBody>
                    <Formik
                        initialValues={{
                            userName: updatedUser?.userName || '',
                            firstName: updatedUser?.firstName || '',
                            surname: updatedUser?.surname || '',
                            role: updatedUser?.role || Role.EMPLOYEE,
                            password: '',
                            confirmPassword: '',
                            mode: updatedUser?.settings?.accountMode || '',
                            defaultZoneId: updatedUser?.settings?.defaultZoneId || null,
                            generateUsername: true,
                            showPasswordInputs: false,
                        }}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ handleChange, handleBlur, values, errors, touched, setFieldValue }) => (
                            <Form>
                                {updatedUser && <Text fontSize={'sm'}>*{t('user.updateUserFormInfo')}</Text>}
                                <VStack>
                                    <FormikSelect
                                        fieldName="role"
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        label={t('role')}
                                        options={UserPolicy.assignableRoles(loggedUser).map((role) => ({
                                            value: role as string,
                                            label: t(role),
                                        }))}
                                        values={values}
                                        errors={errors}
                                        touched={touched}
                                        variant={'filled'}
                                        afterChange={(value: string) => {
                                            if (value === Role.TEAM_ACCOUNT) {
                                                setFieldValue('mode', updatedUser?.settings?.accountMode || AccountMode.ATTENDANCE);
                                            } else {
                                                setFieldValue('mode', undefined);
                                                setFieldValue('defaultZoneId', null);
                                            }
                                        }}
                                    />

                                    {!updatedUser && (
                                        <>
                                            <FormControl mt={4} display="flex" justifyContent={'space-between'} alignItems="center">
                                                <FormLabel htmlFor="generate-username" mb="0">
                                                    {t('user.generateUsername')}
                                                </FormLabel>
                                                <Switch
                                                    id="generate-username"
                                                    isChecked={values.generateUsername}
                                                    onChange={(e) => setFieldValue('generateUsername', e.target.checked)}
                                                />
                                            </FormControl>

                                            {values.generateUsername && (
                                                <Text colorScheme="gray" fontSize="sm">
                                                    {t('user.generateUsernameDescription')}
                                                </Text>
                                            )}
                                            {!values.generateUsername && (
                                                <FormikTextInput
                                                    inputRef={firstField}
                                                    name="userName"
                                                    fieldName="userName"
                                                    handleBlur={handleBlur}
                                                    handleChange={handleChange}
                                                    label={t('user.username')}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    errors={errors}
                                                    values={values}
                                                    touched={touched}
                                                />
                                            )}
                                        </>
                                    )}

                                    <FormikTextInput
                                        inputRef={updatedUser && firstField}
                                        name="firstName"
                                        fieldName="firstName"
                                        handleBlur={handleBlur}
                                        handleChange={handleChange}
                                        label={values.role === Role.TEAM_ACCOUNT ? t('user.accountName') : t('firstName')}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        errors={errors}
                                        values={values}
                                        touched={touched}
                                    />

                                    {values.role !== Role.TEAM_ACCOUNT && (
                                        <FormikTextInput
                                            name="surname"
                                            fieldName="surname"
                                            handleBlur={handleBlur}
                                            handleChange={handleChange}
                                            label={t('surname')}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors}
                                            values={values}
                                            touched={touched}
                                        />
                                    )}

                                    {/* Attendance Settings Section */}
                                    <Text fontSize={'md'} fontWeight={'semibold'} mt={4}>
                                        {t('attendanceSettings')}
                                    </Text>

                                    {/* Default Zone - Available for all roles */}
                                    <Box width={'100%'}>
                                        <FormLabel>{`${t('defaultZone')} (${t('optional').toLocaleLowerCase()})`}</FormLabel>
                                        <ZoneSelectButton
                                            canDelete
                                            onDelete={() => setFieldValue('defaultZoneId', null)}
                                            onSelect={(zone) => {
                                                setFieldValue('defaultZoneId', zone.id);
                                            }}
                                            initialZone={updatedUser?.settings?.defaultZone}
                                        />
                                        {!!(errors.defaultZoneId && touched.defaultZoneId) ? (
                                            <Text color="red.500" fontSize="sm">
                                                {errors.defaultZoneId as string}
                                            </Text>
                                        ) : null}
                                    </Box>

                                    {(!updatedUser || UserPolicy.canUpdatePassword(loggedUser, updatedUser)) && (
                                        <>
                                            {updatedUser && (
                                                <FormControl display="flex" alignItems="center">
                                                    <FormLabel htmlFor="show-password" mb="0">
                                                        {t('user.form.passwordChange')}
                                                    </FormLabel>
                                                    <Switch
                                                        id="show-password"
                                                        isChecked={values.showPasswordInputs}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setFieldValue('showPasswordInputs', checked);
                                                            if (!checked) {
                                                                setFieldValue('password', '');
                                                                setFieldValue('confirmPassword', '');
                                                            }
                                                        }}
                                                    />
                                                </FormControl>
                                            )}
                                            {!updatedUser && <Text fontWeight="semibold">{t('user.form.passwordChange')}</Text>}
                                            {(values.showPasswordInputs || !updatedUser) && (
                                                <>
                                                    <FormikPasswordInput
                                                        errors={errors}
                                                        touched={touched}
                                                        name="password"
                                                        fieldName="password"
                                                        handleChange={handleChange}
                                                        handleBlur={handleBlur}
                                                        label={t(updatedUser ? 'user.newPassword' : 'password')}
                                                        values={values}
                                                    />
                                                    <FormikPasswordInput
                                                        errors={errors}
                                                        touched={touched}
                                                        name="confirmPassword"
                                                        fieldName="confirmPassword"
                                                        handleChange={handleChange}
                                                        handleBlur={handleBlur}
                                                        label={t('confirmPassword')}
                                                        values={values}
                                                    />
                                                </>
                                            )}
                                        </>
                                    )}
                                    <HStack justifyContent={'center'} mt={2}>
                                        <Button
                                            type="submit"
                                            colorScheme="green"
                                            isLoading={useCreateUser.isLoading || useUpdateUser.isLoading}
                                            isDisabled={useCreateUser.isLoading || useUpdateUser.isLoading}
                                        >
                                            {t(updatedUser ? 'save' : 'create')}
                                        </Button>
                                    </HStack>
                                </VStack>
                            </Form>
                        )}
                    </Formik>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
};
